import { useEffect, useId, useMemo, useState, type FormEvent } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button, TextField } from '../ui'
import { useI18n } from '../../i18n'
import adminStyles from '@/styles/pages/AdminPanel.module.scss'

export type OrganizationDeletionTarget = {
  id: string
  name: string
}

type Props = {
  open: boolean
  organization: OrganizationDeletionTarget | null
  submitting?: boolean
  onClose: () => void
  onSubmit: (payload: { confirmationName: string; comment: string }) => Promise<void>
}

function mapApiError(message: string, t: (key: string, fallback?: string) => string): string {
  const normalized = message.trim().toLowerCase()
  if (normalized.includes('confirmation name mismatch')) {
    return t(
      'admin.orgDeletion.nameMismatch',
      'The name you entered does not match the organization name. Copy it exactly, including spaces and capitalization.',
    )
  }
  if (normalized.includes('comment') && normalized.includes('min')) {
    return t(
      'admin.orgDeletion.commentTooShort',
      'Please provide a reason of at least 5 characters. This will be stored in the audit log.',
    )
  }
  if (normalized.includes('already scheduled')) {
    return t(
      'admin.orgDeletion.alreadyScheduled',
      'Deletion is already scheduled for this organization.',
    )
  }
  if (normalized.includes('not found')) {
    return t('admin.orgDeletion.notFound', 'Organization was not found. Refresh the page and try again.')
  }
  return message || t('admin.orgDeletion.genericError', 'Failed to schedule organization deletion.')
}

export default function OrganizationDeletionRequestModal({
  open,
  organization,
  submitting = false,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useI18n()
  const titleId = useId()
  const [confirmationName, setConfirmationName] = useState('')
  const [comment, setComment] = useState('')
  const [nameTouched, setNameTouched] = useState(false)
  const [commentTouched, setCommentTouched] = useState(false)
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    if (!open) return
    setConfirmationName('')
    setComment('')
    setNameTouched(false)
    setCommentTouched(false)
    setSubmitError('')
  }, [open, organization?.id])

  const expectedName = organization?.name ?? ''
  const nameMismatch = nameTouched && confirmationName.trim() !== expectedName
  const commentTooShort = commentTouched && comment.trim().length < 5
  const canSubmit = useMemo(() => {
    if (!organization || submitting) return false
    if (confirmationName.trim() !== expectedName) return false
    if (comment.trim().length < 5) return false
    return true
  }, [organization, submitting, confirmationName, expectedName, comment])

  if (!open || !organization) return null

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setNameTouched(true)
    setCommentTouched(true)
    setSubmitError('')
    if (!canSubmit) return
    try {
      await onSubmit({
        confirmationName: confirmationName.trim(),
        comment: comment.trim(),
      })
    } catch (err) {
      const raw = err instanceof Error ? err.message : ''
      setSubmitError(mapApiError(raw, t))
    }
  }

  return (
    <div
      className={adminStyles.modalBackdrop}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose()
      }}
    >
      <div
        className={adminStyles.modalPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={adminStyles.modalBody}>
          <h3 id={titleId} className={adminStyles.modalTitle}>
            {t('admin.orgDeletion.title', 'Schedule organization deletion')}
          </h3>

          <div className={adminStyles.deletionWarnBanner}>
            <AlertTriangle className={adminStyles.deletionWarnIcon} aria-hidden />
            <div>
              <p className={adminStyles.deletionWarnTitle}>
                {t('admin.orgDeletion.warnTitle', 'This action schedules permanent deletion')}
              </p>
              <p className={adminStyles.deletionWarnBody}>
                {t(
                  'admin.orgDeletion.warnBody',
                  'The organization will be retained for 5 days, then permanently removed. Users and assets linked to this organization may lose access.',
                )}
              </p>
            </div>
          </div>

          <p className={adminStyles.deletionOrgLabel}>
            {t('admin.orgDeletion.organizationLabel', 'Organization')}
          </p>
          <p className={adminStyles.deletionOrgName}>{organization.name}</p>

          <form className={adminStyles.formStack} onSubmit={(e) => void handleSubmit(e)}>
            {submitError ? <div className={adminStyles.alertDanger}>{submitError}</div> : null}

            <div>
              <TextField
                label={t('admin.orgDeletion.confirmNameLabel', 'Type organization name to confirm')}
                value={confirmationName}
                onChange={(v) => {
                  setConfirmationName(v)
                  setNameTouched(true)
                  if (submitError) setSubmitError('')
                }}
                placeholder={organization.name}
                minWidth="100%"
                autoComplete="off"
                invalid={nameMismatch}
                disabled={submitting}
                aria-label={t('admin.orgDeletion.confirmNameLabel', 'Type organization name to confirm')}
              />
              {nameMismatch ? (
                <p className={adminStyles.fieldError} role="alert">
                  {t(
                    'admin.orgDeletion.nameMismatchInline',
                    'Name does not match. Expected: {{name}}',
                    { name: organization.name },
                  )}
                </p>
              ) : (
                <p className={adminStyles.fieldHint}>
                  {t(
                    'admin.orgDeletion.confirmNameHint',
                    'Enter the name exactly as shown above (case and spaces matter).',
                  )}
                </p>
              )}
            </div>

            <div>
              <TextField
                label={t('admin.orgDeletion.commentLabel', 'Reason for deletion (audit log)')}
                value={comment}
                onChange={(v) => {
                  setComment(v)
                  setCommentTouched(true)
                  if (submitError) setSubmitError('')
                }}
                multiline
                rows={4}
                minWidth="100%"
                invalid={commentTooShort}
                disabled={submitting}
                placeholder={t(
                  'admin.orgDeletion.commentPlaceholder',
                  'Describe why this organization should be removed…',
                )}
              />
              {commentTooShort ? (
                <p className={adminStyles.fieldError} role="alert">
                  {t('admin.orgDeletion.commentTooShortInline', 'Minimum 5 characters required.')}
                </p>
              ) : (
                <p className={adminStyles.fieldHint}>
                  {t(
                    'admin.orgDeletion.commentHint',
                    'This comment is mandatory and will be stored in the audit trail.',
                  )}
                </p>
              )}
            </div>

            <div className={adminStyles.modalFooter}>
              <Button type="button" size="small" color="secondary" onClick={onClose} disabled={submitting}>
                {t('admin.cancel', 'Cancel')}
              </Button>
              <Button type="submit" size="small" color="primary" disabled={!canSubmit}>
                {submitting
                  ? t('admin.orgDeletion.submitting', 'Scheduling…')
                  : t('admin.orgDeletion.submit', 'Schedule deletion')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
